import type {
  BarChartSpec,
  ChartSpec,
  HeatmapChartSpec,
  LineChartSpec,
  MixedChartSpec,
  PieChartSpec,
} from "./types";

/**
 * Validate and narrow arbitrary JSON (from a Kai ` ```chart ` fence) into a
 * `ChartSpec`. Returns `null` for anything malformed so callers can fall back
 * to a placeholder instead of rendering a broken chart.
 *
 * Ported from `frontend/src/pages/kai/KaiChart.tsx` to keep the Kai contract
 * identical across the two frontends.
 */
export function parseChartSpec(json: string): ChartSpec | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return null;
  }

  if (!parsed || typeof parsed !== "object") return null;
  const candidate = parsed as { type?: unknown };
  const type = candidate.type;

  if (type === "pie") {
    const pie = parsed as Partial<PieChartSpec>;
    if (!Array.isArray(pie.data) || pie.data.length === 0) return null;
    for (const seg of pie.data) {
      if (!seg || typeof seg.value !== "number" || Number.isNaN(seg.value)) {
        return null;
      }
    }
    return parsed as PieChartSpec;
  }

  if (type === "bar" || type === "line" || type === "mixed") {
    const cart = parsed as Partial<BarChartSpec | LineChartSpec | MixedChartSpec>;
    if (!Array.isArray(cart.series) || cart.series.length === 0) return null;
    for (const s of cart.series) {
      if (!s || !Array.isArray(s.data)) return null;
      for (const d of s.data) {
        if (
          !d ||
          typeof d.x !== "string" ||
          typeof d.y !== "number" ||
          Number.isNaN(d.y)
        ) {
          return null;
        }
      }
    }
    if (cart.yScale != null && cart.yScale !== "linear" && cart.yScale !== "log") {
      delete (parsed as { yScale?: unknown }).yScale;
    }
    if (cart.horizontal != null && typeof cart.horizontal !== "boolean") {
      delete (parsed as { horizontal?: unknown }).horizontal;
    }
    return parsed as BarChartSpec | LineChartSpec | MixedChartSpec;
  }

  if (type === "heatmap") {
    const heat = parsed as Partial<HeatmapChartSpec>;
    if (
      !Array.isArray(heat.rows) ||
      !Array.isArray(heat.columns) ||
      !Array.isArray(heat.values) ||
      heat.rows.length === 0 ||
      heat.columns.length === 0
    ) {
      return null;
    }
    return parsed as HeatmapChartSpec;
  }

  return null;
}
